import React from "react";
import { Link } from "react-router-dom";
import {
    Shield,
    Cpu,
    Network,
    FileCode2,
    Workflow,
    Check,
    ArrowRight,
    Boxes,
} from "lucide-react";
import { Reveal, RevealItem } from "../components/Reveal";
import { SEO, SITE_DOMAIN } from "../components/SEO";
import { Breadcrumb } from "../components/Breadcrumb";
import { BREADCRUMBS } from "../lib/pageMetadata";

/**
 * /zeroconf — marketing landing for the Multi-Vendor Firewall Configurator.
 * Public, crawlable. NOT the gated app (that's /portal).
 */
export const Zeroconf = () => {
    const productSchema = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "@id": `${SITE_DOMAIN}/zeroconf#product`,
        name: "ZeroConf Multi-Vendor Firewall Configurator",
        applicationCategory: "SecurityApplication",
        operatingSystem: "Web",
        url: `${SITE_DOMAIN}/zeroconf`,
        description:
            "7-step guided wizard that generates production-ready firewall configurations for Fortinet FortiGate, WatchGuard Firebox, Zyxel USG/ATP, Netgate pfSense, and a generic CLI fallback. 37 hardware models pre-cataloged.",
        publisher: { "@id": `${SITE_DOMAIN}/#organization` },
        offers: {
            "@type": "Offer",
            name: "Pro",
            price: "19.90",
            priceCurrency: "USD",
            url: `${SITE_DOMAIN}/pricing`,
            availability: "https://schema.org/InStock",
        },
    };

    const steps = [
        "Pick your vendor and hardware model from a curated catalog.",
        "Define your network zones (LAN, DMZ, Guest, IoT, Management, VoIP).",
        "Configure firewall rules with sensible defaults.",
        "Set up VPN tunnels (site-to-site, road warrior).",
        "Review the live preview of your CLI / XML output.",
        "Export production-ready configuration files.",
        "Save and link to a client in your IPAM for future re-use.",
    ];

    const vendors = [
        { name: "Fortinet FortiGate", format: "CLI + XML" },
        { name: "WatchGuard Firebox", format: "CLI" },
        { name: "Zyxel USG / ATP", format: "CLI" },
        { name: "Netgate pfSense", format: "XML import" },
        { name: "Generic CLI fallback", format: "for unsupported vendors" },
    ];

    return (
        <>
            <SEO
                title="Multi-Vendor Firewall Configurator — ZeroConf"
                description="Generate production-ready firewall configurations via a 7-step guided wizard. Fortinet, WatchGuard, Zyxel, pfSense, and a generic CLI fallback. 37 hardware models pre-cataloged."
                canonicalPath="/zeroconf"
                schema={productSchema}
            />
            <div
                className="pt-24 md:pt-28 pb-12 mx-auto max-w-5xl px-5 md:px-10"
                data-testid="zeroconf-page"
            >
                <Breadcrumb items={BREADCRUMBS.zeroconf} />
                <Reveal>
                    <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                        ZeroConf · Pro feature
                    </p>
                    <h1
                        className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tight leading-[1.05] max-w-3xl"
                        data-testid="zeroconf-title"
                    >
                        Multi-Vendor Firewall{" "}
                        <span className="text-accent-cyan">Configurator</span>.
                    </h1>
                    <p
                        className="mt-6 text-[15px] text-text-muted leading-relaxed max-w-2xl"
                        data-testid="zeroconf-subtitle"
                    >
                        Generate production-ready firewall configurations via a 7-step
                        guided wizard. Supports Fortinet FortiGate, WatchGuard Firebox,
                        Zyxel USG / ATP, Netgate pfSense, and a generic CLI fallback.{" "}
                        <strong className="text-text-primary">
                            37 hardware models pre-cataloged.
                        </strong>
                    </p>
                </Reveal>

                {/* How it works */}
                <section className="mt-20" data-testid="zeroconf-how">
                    <Reveal>
                        <div className="flex items-center gap-3 mb-5">
                            <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-canvas border border-accent-cyan/30 text-accent-cyan">
                                <Workflow className="w-4 h-4" />
                            </div>
                            <h2 className="font-display font-semibold text-2xl sm:text-3xl tracking-tight">
                                How it works
                            </h2>
                        </div>
                    </Reveal>
                    <Reveal stagger className="mt-6 space-y-2.5">
                        {steps.map((s, i) => (
                            <RevealItem
                                key={s}
                                className="flex items-start gap-4 rounded-xl border border-white/[0.06] bg-surface/40 backdrop-blur-md p-4"
                            >
                                <span className="inline-flex shrink-0 h-7 w-7 items-center justify-center rounded-md bg-accent-cyan/10 border border-accent-cyan/30 text-accent-cyan text-[12px] font-mono">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <p className="text-[14px] text-text-muted leading-relaxed">
                                    {s}
                                </p>
                            </RevealItem>
                        ))}
                    </Reveal>
                </section>

                {/* Supported vendors */}
                <section className="mt-20" data-testid="zeroconf-vendors">
                    <Reveal>
                        <div className="flex items-center gap-3 mb-5">
                            <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-canvas border border-accent-cyan/30 text-accent-cyan">
                                <Cpu className="w-4 h-4" />
                            </div>
                            <h2 className="font-display font-semibold text-2xl sm:text-3xl tracking-tight">
                                Supported vendors
                            </h2>
                        </div>
                    </Reveal>
                    <Reveal stagger className="mt-6 grid gap-3 sm:grid-cols-2">
                        {vendors.map((v) => (
                            <RevealItem
                                key={v.name}
                                className="rounded-xl border border-white/[0.06] bg-surface/40 backdrop-blur-md p-4 flex items-start gap-3"
                            >
                                <Check className="w-4 h-4 text-accent-mint mt-1 shrink-0" />
                                <div>
                                    <p className="text-[14px] font-medium text-text-primary">
                                        {v.name}
                                    </p>
                                    <p className="text-[12px] font-mono uppercase tracking-[0.18em] text-text-dim mt-0.5">
                                        {v.format}
                                    </p>
                                </div>
                            </RevealItem>
                        ))}
                    </Reveal>
                </section>

                {/* Why */}
                <section className="mt-20" data-testid="zeroconf-why">
                    <Reveal>
                        <div className="flex items-center gap-3 mb-5">
                            <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-canvas border border-accent-cyan/30 text-accent-cyan">
                                <Shield className="w-4 h-4" />
                            </div>
                            <h2 className="font-display font-semibold text-2xl sm:text-3xl tracking-tight">
                                Why this exists
                            </h2>
                        </div>
                        <p className="text-[14.5px] text-text-muted leading-[1.85] max-w-2xl">
                            Every firewall vendor has its own dialect, its own gotchas,
                            and its own 800-page configuration manual. The wizard does the
                            translation work so you can focus on the network design — not
                            the syntax. Outputs are deterministic and reproducible: same
                            inputs, byte-for-byte same config.
                        </p>
                    </Reveal>
                </section>

                {/* Get started */}
                <section className="mt-20" data-testid="zeroconf-cta">
                    <Reveal>
                        <div className="rounded-2xl border border-accent-cyan/30 bg-accent-cyan/[0.04] p-8 md:p-10">
                            <div className="flex items-center gap-3 mb-3">
                                <FileCode2 className="w-5 h-5 text-accent-cyan" />
                                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                                    Get started
                                </p>
                            </div>
                            <h2 className="font-display font-semibold text-2xl sm:text-3xl tracking-tight">
                                Available on the Pro plan — $19.90 / month.
                            </h2>
                            <p className="mt-3 text-[14px] text-text-muted leading-relaxed max-w-2xl">
                                Create a free account to explore the wizard in preview mode.
                                Upgrade when you&apos;re ready to export production configs.
                            </p>
                            <div className="mt-6 flex flex-col sm:flex-row gap-3">
                                <Link
                                    to="/signup"
                                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-accent-cyan text-canvas font-medium text-[13.5px] hover:bg-accent-cyan/90 transition-colors"
                                    data-testid="zeroconf-cta-signup"
                                >
                                    Create a free account
                                    <ArrowRight className="w-3.5 h-3.5" />
                                </Link>
                                <Link
                                    to="/portal/demo"
                                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md border border-white/10 text-text-primary font-medium text-[13.5px] hover:border-white/20 transition-colors"
                                    data-testid="zeroconf-cta-demo"
                                >
                                    See live demo
                                </Link>
                            </div>
                        </div>
                    </Reveal>
                </section>

                {/* Related */}
                <section className="mt-16" data-testid="zeroconf-related">
                    <Reveal>
                        <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-text-dim">
                            Related
                        </p>
                        <div className="mt-3 flex flex-wrap gap-3 text-[13px]">
                            <Link
                                to="/ipam"
                                className="inline-flex items-center gap-1.5 text-accent-cyan hover:underline"
                            >
                                <Network className="w-3.5 h-3.5" />
                                Client IPAM
                            </Link>
                            <Link
                                to="/tools/nis2-assessment"
                                className="inline-flex items-center gap-1.5 text-accent-cyan hover:underline"
                            >
                                <Boxes className="w-3.5 h-3.5" />
                                NIS2 self-assessment
                            </Link>
                            <Link
                                to="/pricing"
                                className="inline-flex items-center gap-1.5 text-accent-cyan hover:underline"
                            >
                                Pricing
                            </Link>
                        </div>
                    </Reveal>
                </section>
            </div>
        </>
    );
};

export default Zeroconf;
